/**
 * Shared Generation Readiness contract (VISTOERA_PC2_SCRIPT_GENERATION_
 * PRE_SUBMIT_READINESS_GATE_AND_REQUIRED_INPUT_VALIDATION_P0_V1).
 *
 * Reusable across Creative / Script / Storyboard / Image / Video / … —
 * this module only defines the shape + merge helpers. Capability-specific
 * evaluators live next to each canvas helper (e.g. scriptGenerationReadiness).
 *
 * Local-only: never hits the network. Backend authoritative validation
 * still runs on submit.
 */

export type GenerationReadinessDimension =
  | 'INPUT_READY'
  | 'PROVIDER_READY'
  | 'SKILL_READY'
  | 'RUNTIME_READY'

export interface GenerationBlockingReason {
  code: string
  /** Internal field id for Admin/debug — never show raw to tenants. */
  field?: string
  /** i18n key under canvas namespace (e.g. scriptGeneration.connectCreativeFirst). */
  messageKey: string
  dimension: GenerationReadinessDimension
}

export interface GenerationReadiness {
  ready: boolean
  blockingReasons: GenerationBlockingReason[]
  warnings: GenerationBlockingReason[]
  dimensions: Record<GenerationReadinessDimension, boolean>
}

const EMPTY_DIMENSIONS: Record<GenerationReadinessDimension, boolean> = {
  INPUT_READY: true,
  PROVIDER_READY: true,
  SKILL_READY: true,
  RUNTIME_READY: true,
}

export function emptyGenerationReadiness(): GenerationReadiness {
  return {
    ready: true,
    blockingReasons: [],
    warnings: [],
    dimensions: { ...EMPTY_DIMENSIONS },
  }
}

export function buildGenerationReadiness(args: {
  blockingReasons?: GenerationBlockingReason[]
  warnings?: GenerationBlockingReason[]
}): GenerationReadiness {
  const blockingReasons = args.blockingReasons ?? []
  const warnings = args.warnings ?? []
  const dimensions: Record<GenerationReadinessDimension, boolean> = { ...EMPTY_DIMENSIONS }
  for (const reason of blockingReasons) {
    dimensions[reason.dimension] = false
  }
  return {
    ready: blockingReasons.length === 0,
    blockingReasons,
    warnings,
    dimensions,
  }
}

/** Prefer INPUT → PROVIDER → SKILL → RUNTIME for the primary footer hint. */
const DIMENSION_PRIORITY: GenerationReadinessDimension[] = [
  'INPUT_READY',
  'PROVIDER_READY',
  'SKILL_READY',
  'RUNTIME_READY',
]

export function primaryBlockingReason(readiness: GenerationReadiness): GenerationBlockingReason | null {
  if (!readiness.blockingReasons.length) return null
  for (const dim of DIMENSION_PRIORITY) {
    const hit = readiness.blockingReasons.find((r) => r.dimension === dim)
    if (hit) return hit
  }
  return readiness.blockingReasons[0] ?? null
}

export function mergeGenerationReadiness(...parts: GenerationReadiness[]): GenerationReadiness {
  const blockingReasons: GenerationBlockingReason[] = []
  const warnings: GenerationBlockingReason[] = []
  const seenBlock = new Set<string>()
  const seenWarn = new Set<string>()
  for (const part of parts) {
    for (const r of part.blockingReasons) {
      const key = `${r.code}:${r.field ?? ''}`
      if (seenBlock.has(key)) continue
      seenBlock.add(key)
      blockingReasons.push(r)
    }
    for (const r of part.warnings) {
      const key = `${r.code}:${r.field ?? ''}`
      if (seenWarn.has(key)) continue
      seenWarn.add(key)
      warnings.push(r)
    }
  }
  return buildGenerationReadiness({ blockingReasons, warnings })
}
